// docs/116 CI10: the noticeboard close up. A cork board in a wooden frame with this week's notes
// pinned at angles; clicking one lifts it off the cork to read, clicking again puts it back.
// docs/121 VW4/VW5: players pin their own notes for each other (signed by one of their heroes, on the
// paper they choose) and take their own down again; a lifted adventure hook can be taken up there and
// then, which calls the band as a village's trouble does (docs/120 VM6).
import { useMemo, useState } from 'react';
import { Button } from '../../../../components';
import { playSfx } from '../../../../../core/audio';
import { noticesFor, type Notice } from '../notices';
import { NOTE_PAPERS, NOTICE_MAX, NOTICE_MINE, NOTICE_TITLE_MAX, type InnData, type NotePaper } from '../types';
import type { VillageLook } from '../../types';
import { HeroPick, Scene, firstPayer, useOpenSound } from './common';

const PAPER_NAME: Record<NotePaper, string> = { parchment: 'Parchment', scrap: 'A scrap', wanted: 'Wanted poster', bill: 'A bill', card: 'A card' };

function NoteBody({ n }: { n: Notice }) {
  if (n.kind === 'hook') {
    return <>
      <span className="fk-innp__note-eyebrow">Help wanted</span>
      <b className="fk-innp__note-title">{n.title}</b>
      <span className="fk-innp__note-body">“{n.body}”</span>
      <span className="fk-innp__note-place">at {n.place}</span>
      <span className="fk-innp__note-sign">— {n.sign}</span>
    </>;
  }
  return <>
    {n.kind === 'player' && n.paper !== 'wanted' && <span className="fk-innp__note-eyebrow fk-innp__note-eyebrow--player">{n.mine ? 'Your note' : 'From a traveller'}</span>}
    {n.title && <b className="fk-innp__note-title">{n.title}</b>}
    <span className="fk-innp__note-body">{n.body}</span>
    {n.foot && <span className="fk-innp__note-foot">{n.foot}</span>}
    {n.sign && <span className="fk-innp__note-sign">— {n.sign}</span>}
  </>;
}

/** The little card a note is written on before it goes up. */
function PinForm({ data, onDone }: { data: InnData; onDone(): void }) {
  const [hero, setHero] = useState(() => firstPayer(data.payers)?.id);
  const [paper, setPaper] = useState<NotePaper>('parchment');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [bad, setBad] = useState<string>();
  const signer = data.payers.find((p) => p.id === hero) ?? data.payers[0];
  const mine = data.notices.filter((n) => n.mine).length;
  const pin = async () => {
    const t = text.trim();
    if (!signer || !t) return;
    setBusy(true);
    playSfx('inn.quill', { gain: 0.6 });
    const r = await data.act.pin(signer.id, title.trim().slice(0, NOTICE_TITLE_MAX), t.slice(0, NOTICE_MAX), paper);
    setBusy(false);
    if (r === true) { playSfx('inn.cards', { gain: 0.6 }); setTitle(''); setText(''); setBad(undefined); onDone(); }
    else { playSfx('ui.error'); setBad(`${r[0]?.toUpperCase() ?? ''}${r.slice(1)}.`); }
  };
  return (
    <div className={`fk-innp__pinform fk-innp__note--${paper}`} data-testid="notice-form" onClick={(e) => e.stopPropagation()}>
      <b className="fk-innp__write-head">Pin a note</b>
      <HeroPick payers={data.payers} value={signer?.id} onChange={setHero} label="Signed by" testid="notice-hero" />
      <div className="fk-innp__papers" role="radiogroup" aria-label="The paper">
        {NOTE_PAPERS.map((p) => (
          <button key={p} type="button" role="radio" aria-checked={paper === p} className={`fk-innp__paper fk-innp__note--${p} ${paper === p ? 'is-on' : ''}`} onClick={() => setPaper(p)} title={PAPER_NAME[p]} data-testid="notice-paper" data-paper={p}>
            <span className="fk-innp__paper-name">{PAPER_NAME[p]}</span>
          </button>
        ))}
      </div>
      <input className="fk-innp__ink fk-innp__ink--title" value={title} maxLength={NOTICE_TITLE_MAX} placeholder="A heading (if you like)"
        onChange={(e) => setTitle(e.target.value.slice(0, NOTICE_TITLE_MAX))} onKeyDown={(e) => e.stopPropagation()} aria-label="The note's heading" data-testid="notice-title" disabled={!signer} />
      <textarea className="fk-innp__ink" value={text} maxLength={NOTICE_MAX} rows={4} placeholder="Meet at the old mill at dusk. Bring rope."
        onChange={(e) => setText(e.target.value.slice(0, NOTICE_MAX))} onKeyDown={(e) => e.stopPropagation()} aria-label="What the note says" data-testid="notice-text" disabled={!signer} />
      <span className="fk-innp__write-foot">
        <span className={`fk-innp__count ${text.length >= NOTICE_MAX ? 'is-full' : ''}`}>{text.length}/{NOTICE_MAX}</span>
        <Button small variant="ghost" onClick={onDone} data-testid="notice-cancel">Put it away</Button>
        <Button small primary disabled={busy || !signer || !text.trim()} onClick={() => void pin()} data-testid="notice-pin">Pin it up</Button>
      </span>
      {bad && <span className="fk-innp__said is-bad" role="status">{bad}</span>}
      <span className="fk-innp__faint">{mine >= NOTICE_MINE ? `You have ${NOTICE_MINE} notes up: your oldest comes down for this one.` : 'Everyone in this World who stops at this inn can read it.'}</span>
    </div>
  );
}

export function NoticeBoard({ data, look }: { data: InnData; look: VillageLook }): React.JSX.Element {
  useOpenSound('inn.cards', 0.5);
  const notes = useMemo(() => noticesFor({ seed: look.seed, week: data.week, village: look.name, rumours: data.rumours, hooks: data.hooks, notes: data.notices }), [look.seed, look.name, data.week, data.rumours, data.hooks, data.notices]);
  const [lifted, setLifted] = useState<string>();
  const [writing, setWriting] = useState(false);
  const [busy, setBusy] = useState(false);
  const lift = (id: string) => {
    playSfx(lifted === id ? 'inn.cards' : 'inn.page', { gain: 0.5 });
    setLifted((l) => (l === id ? undefined : id));
  };
  const takeDown = async (n: Notice) => {
    if (n.noteId === undefined) return;
    setBusy(true);
    const ok = await data.act.unpin(n.noteId);
    setBusy(false);
    if (ok) { playSfx('inn.cards', { gain: 0.6 }); setLifted(undefined); } else playSfx('ui.error');
  };
  const takeUp = async (n: Notice) => {
    if (n.offerId === undefined || !data.act.takeUp) return;
    setBusy(true);
    const ok = await data.act.takeUp(n.offerId);
    setBusy(false);
    if (ok) setLifted(undefined);
  };
  const players = data.notices.length;
  return (
    <Scene name="notice" label="The noticeboard" className="fk-innp__scene--notice"
      caption={<>The noticeboard of {look.name || 'the village'} · changes every week{data.hooks.length ? ` · ${data.hooks.length} asking for help` : ''}{players ? ` · ${players} from travellers` : ''}
        {' '}<Button small variant="ghost" onClick={() => { playSfx('inn.page', { gain: 0.5 }); setLifted(undefined); setWriting((w) => !w); }} disabled={!data.payers.length} data-testid="notice-write">✎ Pin a note</Button></>}>
      <div className="fk-innp__board" onClick={(e) => { if (e.target === e.currentTarget) { setLifted(undefined); setWriting(false); } }}>
        <span className="fk-innp__board-plate" aria-hidden="true">Notices</span>
        {notes.map((n, i) => {
          const up = lifted === n.id;
          return (
            <div key={n.id} role="button" tabIndex={0} data-testid="inn-notice" data-kind={n.kind} data-mine={n.mine ? 'true' : undefined}
              className={`fk-innp__note fk-innp__note--${n.paper}${n.kind === 'player' ? ' fk-innp__note--player' : ''} ${up ? 'is-lifted' : ''} ${(lifted && !up) || writing ? 'is-behind' : ''}`}
              style={{ left: `${n.x * 100}%`, top: `${n.y * 100}%`, '--tilt': `${n.tilt}deg`, zIndex: up ? 50 : i + 1 } as React.CSSProperties}
              aria-pressed={up} aria-label={`${n.title ?? 'A note'}: ${n.body}`} onClick={() => lift(n.id)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); lift(n.id); } }}>
              <span className={`fk-innp__pin fk-innp__pin--${n.pin}`} aria-hidden="true" />
              <NoteBody n={n} />
              {up && n.kind === 'hook' && n.offerId !== undefined && data.act.takeUp && (
                <span className="fk-innp__note-act"><Button small primary disabled={busy} onClick={(e) => { e.stopPropagation(); void takeUp(n); }} data-testid="notice-take-up">Take this up</Button></span>
              )}
              {up && n.kind === 'player' && n.mine && (
                <span className="fk-innp__note-act"><Button small variant="ghost" disabled={busy} onClick={(e) => { e.stopPropagation(); void takeDown(n); }} data-testid="notice-unpin">Take it down</Button></span>
              )}
            </div>
          );
        })}
        {writing && <PinForm data={data} onDone={() => setWriting(false)} />}
      </div>
    </Scene>
  );
}
